import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import styles from './index.module.scss';
import ReviewPlanDescriptionDialog from './review-plan-details-description-dialog';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import CardAction from '@components-clean/core/card/card-actions';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextBoxController } from '@components-clean/core/text-box';
import { useOnValueChange, useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { EditReviewDto } from '@interfaces-clean/reviews.interface';
import { StudyMemberDto } from '@interfaces-clean/study-member.interface';
import { isValidReviewTitle } from '@utils-clean/validation';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import { doFormat } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_SM_STUDY_MEMBERS } from '@queries/studies';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

export type ReviewersMultiSelectListItem = {
    id: string;
    name: string;
    active: boolean;
    groupedBy: 'Active' | 'Inactive';
    disabled: boolean;
};

export type ReviewPlanForm = {
    title: string;
    reviewers: ReviewersMultiSelectListItem[];
};

type Props = {
    canEdit: boolean;
    review: EditReviewDto;
    smStudyMembers: StudyMemberDto[];
    studyRef: string;
};

export const COMPONENT_NAME = 'active-review-review-plan-section';

const ReviewPlanSection = ({ canEdit, review, smStudyMembers, studyRef }: Props) => {
    const { t } = useTranslation();
    const { t: tReviewPlan } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.reviewPlan.reviewPlan',
    });
    const user = useAuthorization();
    const userId = user?.id;

    const { trigger: updateReview, isMutating: isUpdatingReview } = SubjectMonitoringReviewAPI.useUpdateReview(
        studyRef,
        review.recordId!,
    );

    const getAllReviewsQuery = SubjectMonitoringReviewAPI.useGetAllReviewsQuery(studyRef);

    // Get list of all sm study members for current study
    const getSmStudyMembersQuery = useQuery(GET_SM_STUDY_MEMBERS, { variables: { studyId: studyRef } });
    // Try to get id of current user's study membership, if they are a member (this is different from their user id)
    const smStudyMemberId = useMemo(() => {
        if (userId == null) {
            return undefined;
        }

        // Try to find sm study member with userId of current user
        const matchedUser = getSmStudyMembersQuery.data?.smStudyMembers?.find((m) => m?.userId === userId);

        // Return the id of the membership record
        return matchedUser?.id as string | undefined;
    }, [userId, getSmStudyMembersQuery.data]);

    const isEditMode = useBoolean(false);
    const descriptionDialogOpen = useBoolean(false);

    // Get list of all possible sm study members for Review Plan Reviewers MultiSelect field
    const smStudyMembersListOptions = useMemo<ReviewersMultiSelectListItem[]>(() => {
        const reviewersOnLoadIdSet = new Set(
            review?.reviewers
                ?.map((x) => x.ref)
                // Remove any which are somehow null
                .filter((x) => x != null),
        );

        return (
            smStudyMembers
                // Remove bad data (since everything is nullable...)
                .filter((x) => typeof x.ref === 'string' && typeof x.name === 'string' && typeof x.acitve === 'boolean')
                .map((x) => ({
                    id: x.ref!,
                    name: x.name!,
                    active: x.acitve!,
                    groupedBy: x.acitve ? 'Active' : 'Inactive',
                    // Not active and not already selected on load
                    disabled: reviewersOnLoadIdSet.has(x.ref!) ? false : !x.acitve,
                }))
        );
    }, [review, smStudyMembers]);

    const defaultValues = useMemo<ReviewPlanForm>(() => {
        const smStudyMembersByIdMap = new Map(smStudyMembersListOptions.map((x) => [x.id, x]));

        // Map current reviewers to selected list items
        const reviewers = (review?.reviewers ?? [])
            .map((x) => smStudyMembersByIdMap.get(x.ref!))
            // Remove any that fail to map. This shouldn't be reached since SM Study Members are never deleted
            .filter((x) => x != null);

        return {
            title: review?.title ?? '',
            reviewers,
        };
    }, [review, smStudyMembersListOptions]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<ReviewPlanForm>({
        defaultValues,
        mode: 'all',
    });

    useOnValueChange(defaultValues, () => {
        reset(defaultValues, { keepDirty: false });
    });

    const createdByText =
        review.createdByName != null && review.createdDate != null
            ? `${review.createdByName} ${doFormat(new Date(review.createdDate))}`
            : null;

    const updatedByText =
        review.updatedByName != null && review.updatedDate != null
            ? `${review.updatedByName} ${doFormat(new Date(review.updatedDate))}`
            : null;

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const canSave = isDirty && isValid && !isSubmitting;

    const onCancel = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        reset();
        isEditMode.setFalse();
    };

    const onSave = async () => {
        await handleSubmit(async (saveData) => {
            try {
                await updateReview({
                    type: review.type!,
                    title: saveData.title,
                    datasetId: review.datasetId!,
                    reviewed: review.reviewed!,
                    reviewStartDate: review.reviewStartDate,
                    reviewEndDate: review.reviewEndDate,
                    reviewSummary: review.reviewSummary,
                    studyMemberRefs: saveData.reviewers.map((x) => x.id),
                });
                ShowSuccess(t('actionsSuccessfulToast'));
            } catch {
                ShowError(t('errorMessage'));
            } finally {
                isEditMode.setFalse();
            }
        })();
    };

    return (
        <>
            {/* Manual EditCard, since setting `headerActions` on EditCard makes it not show the edit/save/cancel buttons */}
            {/* And we need a custom header button here in addition to the usual edit/save/cancel buttons */}
            <Card
                data-testid="review-details-card"
                className={cn(styles.gridCell, styles.reviewPlanSection)}
                bodyClassName={cn(styles.gridCellInner, styles.reviewPlanSectionInner)}
                data-type="review-details"
                title={tReviewPlan('title')}
                headerActions={
                    <div className={styles.editOptions}>
                        {canEdit &&
                            (isEditMode.value ? (
                                <>
                                    <Button
                                        data-testid="review-details-card-cancel-button"
                                        onClick={onCancel}
                                        disabled={isUpdatingReview}
                                    >
                                        {t('cancel')}
                                    </Button>
                                    <Button
                                        data-testid="review-details-card-save-button"
                                        disabled={!canSave || isUpdatingReview}
                                        primary
                                        onClick={onSave}
                                    >
                                        {t('save')}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    data-testid="review-details-card-edit-button"
                                    primary
                                    onClick={isEditMode.setTrue}
                                    disabled={getAllReviewsQuery.isLoading}
                                >
                                    {t('edit')}
                                </Button>
                            ))}

                        <CardAction
                            data-testid="open-description-button"
                            className={styles.openDescriptionDialogButton}
                            faIcon={['far', 'memo-circle-info']}
                            onClick={descriptionDialogOpen.setTrue}
                            title={tReviewPlan('title')}
                        />
                    </div>
                }
                padding="none"
            >
                <div className={styles.reviewPlanCardHeader}>
                    <div className={styles.reviewPlanCardHeaderSection}>
                        <span className={styles.reviewPlanCardHeaderTitle}>{tReviewPlan('type')}</span>
                        <span className={styles.reviewPlanCardHeaderValue}>{review.type}</span>
                    </div>
                    <div className={styles.reviewPlanCardHeaderSection}>
                        <span className={styles.reviewPlanCardHeaderTitle}>{tReviewPlan('dataset')}</span>
                        <span className={styles.reviewPlanCardHeaderValue}>{review.datasetDate}</span>
                    </div>
                </div>

                <div className={styles.reviewPlanCardMain}>
                    <TextBoxController
                        data-testid="review-plan-title"
                        control={control}
                        name="title"
                        label={tReviewPlan('fields.title')}
                        maxLength={30}
                        required
                        disabled={!isEditMode.value}
                        validateRules={{
                            noInvalidCharacters: (value) => {
                                if (value && !isValidReviewTitle(value as string)) {
                                    return tReviewPlan('invalidChars');
                                }
                                return true;
                            },
                            checkExistingReviews: (value) => {
                                if (value) {
                                    const trimmed = (value as string).trim().toLowerCase();
                                    if (
                                        getAllReviewsQuery.data?.some(
                                            (rev) =>
                                                rev?.id !== review.id &&
                                                rev?.title?.toLowerCase() === trimmed &&
                                                rev.datasetId === review.datasetId &&
                                                rev.type === review.type,
                                        )
                                    ) {
                                        return tReviewPlan('reviewAlreadyExists');
                                    }
                                }
                                return true;
                            },
                        }}
                    />

                    <MultiSelectController
                        data-testid="review-plan-reviewrs"
                        isEditing
                        control={control}
                        name="reviewers"
                        label={tReviewPlan('fields.reviewers')}
                        data={smStudyMembersListOptions}
                        dataItemKey="id"
                        textField="name"
                        activeField="active"
                        allowCustom={false}
                        maxLines={2}
                        disabled={!isEditMode.value}
                        validateRules={{
                            cannotRemoveSelf: (value) => {
                                let updatedValues = value as ReviewersMultiSelectListItem[] | null;
                                if (!updatedValues) {
                                    updatedValues = [];
                                }
                                const oldReviewerRefs = new Set(review.reviewers?.map((x) => x.ref));
                                const newReviewerRefs = new Set(updatedValues.map((x) => x.id));
                                const currentUserRemovedFromActiveReview =
                                    typeof smStudyMemberId !== 'undefined' &&
                                    oldReviewerRefs.has(smStudyMemberId) &&
                                    !newReviewerRefs.has(smStudyMemberId);

                                if (currentUserRemovedFromActiveReview) return tReviewPlan('cannotRemoveSelf');

                                return true;
                            },
                        }}
                    />
                </div>

                {(createdByText != null || updatedByText != null) && (
                    // Only include if at least 1 of created by / updated by is set
                    <div className={styles.reviewPlanCardFooter}>
                        <div className={styles.reviewPlanCardFooterTitle}>{tReviewPlan('created')}</div>
                        <div className={styles.reviewPlanCardFooterValue}>{createdByText}</div>
                        <div className={styles.reviewPlanCardFooterTitle}>{tReviewPlan('updated')}</div>
                        <div className={styles.reviewPlanCardFooterValue}>{updatedByText}</div>
                    </div>
                )}
            </Card>

            {descriptionDialogOpen.value && review && (
                <ReviewPlanDescriptionDialog
                    name={review.type}
                    description={review.reviewPlanDescription}
                    closeFn={descriptionDialogOpen.setFalse}
                />
            )}
        </>
    );
};

export default ReviewPlanSection;
